import React, { useEffect, useRef, useState } from "react";

const ServiceCard = ({ service, index }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
        );
        if (cardRef.current) observer.observe(cardRef.current);
        return () => observer.disconnect();
    }, []);

    const accentColors = {
        "Custom Software Development": "from-blue-500/20 to-cyan-500/20 text-blue-400 border-blue-500/30",
        "Cloud & Infrastructure Solutions": "from-sky-500/20 to-blue-500/20 text-sky-400 border-sky-500/30",
        "Cybersecurity Solutions": "from-emerald-500/20 to-teal-500/20 text-emerald-400 border-emerald-500/30",
        "System Integration": "from-violet-500/20 to-purple-500/20 text-violet-400 border-violet-500/30",
        "Business Automation": "from-amber-500/20 to-orange-500/20 text-amber-400 border-amber-500/30",
        "IT Consultancy": "from-rose-500/20 to-pink-500/20 text-rose-400 border-rose-500/30",
        "Hardware & Networking": "from-slate-500/20 to-gray-500/20 text-slate-400 border-slate-500/30",
        "EFRIS Integration": "from-amber-500/20 to-yellow-500/20 text-amber-400 border-amber-500/30",
        "SAP Business One Solutions": "from-blue-600/20 to-indigo-500/20 text-blue-400 border-blue-600/30",
    };

    const colorClass = accentColors[service.title] || accentColors["IT Consultancy"];

    return (
        <div
            ref={cardRef}
            className={`group relative bg-slate-900/80 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 
        hover:border-slate-600 hover:-translate-y-2 hover:shadow-2xl hover:shadow-amber-500/5
        transition-all duration-500 ease-out
        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
            style={{ transitionDelay: `${index * 75}ms` }}
        >
            <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${colorClass.split(" ").slice(0, 2).join(" ")} opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-10`} />

            <div className="flex items-start justify-between mb-6">
                <div className={`w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${colorClass} border text-2xl shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <i className={service.icon} aria-hidden="true" />
                </div>
                {service.badge && (
                    <span className="text-[10px] uppercase tracking-wider bg-amber-500/10 text-amber-400 px-3 py-1 rounded-full border border-amber-500/20 font-semibold whitespace-nowrap">
                        {service.badge}
                    </span>
                )}
            </div>

            <h3 className="text-xl font-bold text-white mb-3 group-hover:text-amber-400 transition-colors duration-300">
                {service.title}
            </h3>

            <p className={`text-slate-400 text-sm leading-relaxed ${isExpanded ? "" : "line-clamp-3"}`}>
                {service.description}
            </p>

            <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-6 text-sm font-semibold text-amber-400 hover:text-amber-300 flex items-center gap-2 group/btn"
                aria-expanded={isExpanded}
            >
                {isExpanded ? "Show Less" : "Learn More"}
                <i className={`bi bi-arrow-right transition-transform duration-300 group-hover/btn:translate-x-1 ${isExpanded ? "rotate-90" : ""}`} aria-hidden="true" />
            </button>
        </div>
    );
};

export default function Services() {
    const [headerVisible, setHeaderVisible] = useState(false);
    const headerRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setHeaderVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold: 0.2 }
        );
        if (headerRef.current) observer.observe(headerRef.current);
        return () => observer.disconnect();
    }, []);

    const services = [
        {
            icon: "bi bi-code-square",
            title: "Custom Software Development",
            description: "Design and development of scalable web applications, mobile apps, and enterprise platforms that solve real business challenges. From MVPs to full-scale systems, we build software that grows with you.",
            badge: "Core Service"
        },
        {
            icon: "bi bi-cloud-check",
            title: "Cloud & Infrastructure Solutions",
            description: "Secure cloud deployment, server management, hosting, backups, and infrastructure optimization for reliable business operations. We ensure 99.9% uptime and disaster recovery readiness."
        },
        {
            icon: "bi bi-shield-lock",
            title: "Cybersecurity Solutions",
            description: "Protect your digital assets through security assessments, access control, monitoring, and proactive threat prevention. Stay ahead of vulnerabilities with our managed security services."
        },
        {
            icon: "bi bi-diagram-3",
            title: "System Integration",
            description: "Connect your business systems using APIs and automation solutions for seamless data exchange and improved productivity. Break down silos between your ERP, CRM, and e-commerce platforms."
        },
        {
            icon: "bi bi-bar-chart-line",
            title: "Business Automation",
            description: "Automate repetitive processes, improve efficiency, and transform manual workflows into intelligent digital solutions. Reduce operational costs by up to 40% with smart automation."
        },
        {
            icon: "bi bi-briefcase",
            title: "IT Consultancy",
            description: "Technology advisory, digital transformation planning, software reviews, and solutions designed around your business goals. We help you make informed technology investments."
        },
        {
            icon: "bi bi-box-seam",
            title: "Hardware & Networking",
            description: "Enterprise hardware supply, network installation, configuration, and maintenance for reliable IT environments. Structured cabling, wireless solutions, and server room setup."
        },
        {
            icon: "bi bi-receipt",
            title: "EFRIS Integration",
            description: "Integration of ERP and business applications with Uganda Revenue Authority EFRIS for automated electronic invoicing and compliance. Stay tax-compliant with real-time fiscal reporting.",
            badge: "Specialized"
        },
        {
            icon: "bi bi-building-gear",
            title: "SAP Business One Solutions",
            description: "Implementation, customization, reporting, and support services to help organizations manage operations efficiently. From installation to user training and ongoing support.",
            badge: "Enterprise"
        }
    ];

    return (
        <section
            id="services"
            className="py-24 bg-slate-950 text-white relative overflow-hidden"
            aria-labelledby="services-heading"
        >
            <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-600/10 blur-[120px] rounded-full animate-pulse" />
            <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-amber-500/10 blur-[120px] rounded-full animate-pulse" style={{ animationDelay: "2s" }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/5 blur-[150px] rounded-full" />

            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div
                    ref={headerRef}
                    className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                >
                    <span className="inline-flex items-center gap-2 text-amber-500 uppercase tracking-widest text-sm font-semibold font-heading mb-4">
                        <span className="w-8 h-[1px] bg-amber-500/50" />
                        Our Expertise
                        <span className="w-8 h-[1px] bg-amber-500/50" />
                    </span>

                    <h2 id="services-heading" className="text-4xl md:text-5xl lg:text-6xl font-extrabold font-heading leading-tight ">
                        Digital Solutions Built
                        <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600">
                            For Growth
                        </span>
                    </h2>

                    <p className="mt-6 text-slate-400 text-lg leading-relaxed">
                        We help businesses modernize their operations through innovative software,
                        secure infrastructure, and intelligent technology solutions tailored to East African markets.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {services.map((service, index) => (
                        <ServiceCard key={service.title} service={service} index={index} />
                    ))}
                </div>

                <div className="mt-10 relative group">
                    <div className="absolute   duration-500" />
                    <div className="relative p-5 md:p-14 text-center overflow-hidden">
                        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_80%)]" />
                        <div className="relative z-10">
                            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                Ready to Transform Your Business?
                            </h3>
                            <p className="text-slate-400 max-w-2xl mx-auto mb-8 text-lg">
                                Talk to our technology experts and discover the right solution
                                for your organization. Free initial consultation for new clients.
                            </p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                <a
                                    href="https://wa.me/256704698298"
                                    className="inline-flex items-center gap-2 bg-amber-500 text-slate-950 px-8 py-4 rounded-full font-bold hover:bg-amber-400 hover:shadow-lg hover:shadow-amber-500/25 transition-all duration-300 hover:-translate-y-0.5 animate-pulse"
                                >
                                    Whatsapp                                    <i className="bi bi-whatsapp" aria-hidden="true" />
                                </a>
                                <a
                                    href="tel:+256704698298"
                                    className="inline-flex items-center gap-2 bg-slate-800 text-white px-8 py-4 rounded-full font-semibold hover:bg-slate-700 border border-slate-700 hover:border-slate-600 transition-all duration-300"
                                >
                                    <i className="bi bi-telephone-fill text-amber-400" aria-hidden="true" />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}